import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { PIC, SVG_MUI } from "@ryde/assets";
import {
  BulkActionsPanel,
  IBulkActionItem,
  useGenericExportBulkAction,
} from "@ryde/controls";
import { usePageContext, usePassengersBulkActions } from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  DrawerType,
  selectedTableRowsIdsAtomFamily,
  systemSettingsDtoAtom,
  useDrawers,
} from "@ryde/shared";

import { LayoutRight } from "../../../right";
import { LayoutRightCrudContentStatic } from "../../../right/layout.right.crud.content.static";

export const BulkOperationsPassengersDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();

  const selectedIds = useRecoilValue(selectedTableRowsIdsAtomFamily(entity));
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const useTemplates = systemSettingsDto?.metadata?.useTemplates;
  const { openDrawer } = useDrawers();
  const genericExportAction = useGenericExportBulkAction();

  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );

  const { handleBulkRecalculateWalkingDistance } = usePassengersBulkActions();

  const handleBulkPassengerFindNearbyStation = useEvent(async () => {
    if (selectedIds.length) {
      openDrawer({
        drawerProps: {
          type: DrawerType.BulkPassengerNearBySearch,
        },
      });
    }
  });

  const handleActivateShiftTemplate = useEvent(() => {
    if (selectedIds.length) {
      openDrawer({
        drawerProps: {
          type: DrawerType.BulkActivateShiftTemplate,
          selectedPassengersIds: selectedIds,
        },
      });
    }
  });

  const handleRecalculateWalkingDistance = useEvent(async () => {
    if (selectedIds.length) {
      await handleBulkRecalculateWalkingDistance(selectedIds as number[]);
    }
  });

  const actions: IBulkActionItem[] = [
    {
      key: "updateNearbyStation",
      label: t(`general.NEARBY_STATION.UPDATE`),
      hint: t(`passenger.bulk.hints.updateNearbyStation`),
      icon: SVG_MUI.NearbyLocation2SVG,
      onClick: handleBulkPassengerFindNearbyStation,
    },
    {
      key: "recalculateWalkingDistance",
      label: t(`general.RECALCULATE_WALKING_DISTANCE`),
      hint: t(`passenger.bulk.hints.recalculateWalkingDistance`),
      icon: PIC.WalkMenSvg,
      onClick: handleRecalculateWalkingDistance,
    },
    {
      key: "activateShiftTemplate",
      label: t(`general.ACTIVATE_SHIFT_TEMPLATE`),
      hint: t(`passenger.bulk.hints.activateShiftTemplate`),
      icon: SVG_MUI.Calendar3SVG,
      isHidden: !useTemplates,
      onClick: handleActivateShiftTemplate,
    },
    genericExportAction,
  ];

  return (
    <LayoutRight
      headerTitle={t(`general.BULK_ACTIONS`)}
      showFooter={false}
      closeClick={() => setBulkOperationsDrawerOpened?.(false)}
      showBackgroundImage={false}
      entity={"booking"}
    >
      <LayoutRightCrudContentStatic isFlush>
        <BulkActionsPanel actions={actions} />
      </LayoutRightCrudContentStatic>
    </LayoutRight>
  );
};
